import { TabContext, TabList, TabPanel } from "@mui/lab";
import { Autocomplete, Box, Tab, TextField } from "@mui/material";
import React, { useEffect, useRef, useState } from "react";
import {
  getAllConfiguartions,
  sendConfigAnalyticsEvent,
} from "../../services/updateMetaConfigService";
import "./UpdateMetaConfig.css";
import { Loader } from "../../components/loader/Loader";
import {
  newConfigurationType,
  updateConfigurationType,
  updateRequestBody,
} from "./UpdateMetaConfigTypes";
import { GenericPopUp } from "../../components/popup/genericPopUp";
import { popupType } from "../../types/types";
import { ConfirmationPopUp } from "../../components/popup/confirmationPopUp";
import { validateWithOtp } from "../../stateManagement/actions/validateOtpPopupActions";
import { manageUpdateAccessObj } from "../manageUpdateAccess/manageUpdateAccessTypes";
import { TranslationEnum, usePortalTranslation } from "../../providers/TranslationProvider";
import { getLob } from "../../services/authenticationService";

const CUR_COMPONENT = "Update Configuration"
const CUR_COMPONENT_COMMON="commonPortal";
const UpdateConfiguration = ({
  configurations: { allConfigurations, setAllConfigurations },
  openPopUp,
  isJsonString,
  updateConfirmationPopData,
  setConfirmationPopUp,
  editRequestBody: { updateRequestBody, setUpdateRequestBody },
  domainValuesDropDown: { domainValueOptions, setDomainValueOptions },
}: updateConfigurationType) => {
  const { translate } = usePortalTranslation();
  const domainName = allConfigurations?.reduce(
    (uniqueNames: string[], config: any) => {
      if (!uniqueNames.includes(config.domainName)) {
        uniqueNames.push(config.domainName);
      }
      return uniqueNames;
    },
    []
  );

  const [error, setError] = useState(false);

  const updateLocalConfiguration = (type: string) => {
    let newConfiguration;
    if (type === "update") {
      newConfiguration = allConfigurations?.map((eachConfig: any) => {
        if (
          eachConfig.domainType === updateRequestBody.domainType &&
          eachConfig.domainType === updateRequestBody.domainType
        ) {
          eachConfig.domainValues = JSON.parse(updateRequestBody.domainValues);
        }
        return eachConfig;
      });
    } else {
      newConfiguration = allConfigurations?.filter((eachConfig: any) => {
        return (
          eachConfig.domainType !== updateRequestBody.domainType &&
          eachConfig.domainType !== updateRequestBody.domainType
        );
      });
    }
    setAllConfigurations(newConfiguration);
  };

  const checkAllfieldsPresent = () => {
    if (
      !isJsonString(updateRequestBody.domainValues) ||
      !updateRequestBody.domainType ||
      !updateRequestBody.domainName
    ) {
      setError(true);
      return false;
    }
    return true;
  };

  const sendUpdateRequest = async (id: string) => {
    try {
      const requestBody = {
        lob: JSON.parse(localStorage.authContext).user.lob,
        domainName: updateRequestBody.domainName,
        domainType: updateRequestBody.domainType,
        domainValues: JSON.parse(updateRequestBody.domainValues),
      };
      const api =  `/metadata/${updateRequestBody.domainName}?name=${updateRequestBody.domainType}`;
      const apiType = id === "update"? "PUT" : "POST";
      validateWithOtp((verifiedUser?: manageUpdateAccessObj,verifyResponse?: any) => {
        try{
          if (verifyResponse.status === 201 || verifyResponse.status === 200) {
            updateLocalConfiguration("update");
            openPopUp("Your information has been updated!", "Success");
          } else if (verifyResponse.status === 204) {
            updateLocalConfiguration("delete");
            setUpdateRequestBody({
              domainName: "",
              domainType: "",
              domainValues: "",
            });
            sendConfigAnalyticsEvent("config_updated",requestBody,verifyResponse,"Kpi-dashboard-updateMetaConfig",verifiedUser);
            openPopUp("Your data deleted succesfully..!", "Success");
          } else {
            throw new Error();
          }
        }catch(error){
          openPopUp("Something went wrong!", "Error");
        }
      },["demo","prod","prod-egtm"],"admin",api,requestBody,apiType);
    } catch (error) {
      openPopUp("Something went wrong!", "Error");
    }
  };

  return (
    <>
      <div className="update-configuration-label">
        <span>{translate(TranslationEnum.update_configuration,"Update Configuration")}</span>
      </div>
      <div className="update-configuration-inputs">
        <Autocomplete
          sx={{ width: "100%" }}
          value={updateRequestBody.domainName}
          disableClearable
          onChange={(event, value: string | null) => {
            if (value) {
              setError(false);
              const domainValueArray = allConfigurations?.reduce(
                (uniqueValues: string[], config: any) => {
                  if (
                    !uniqueValues.includes(config.domainType) &&
                    config.domainName === value
                  ) {
                    uniqueValues.push(config.domainType);
                  }
                  return uniqueValues;
                },
                []
              );
              setDomainValueOptions(domainValueArray);

              setUpdateRequestBody({
                domainName: value,
                domainType: "",
                domainValues: "",
              });
            }
          }}
          options={domainName}
          renderInput={(params) => (
            <TextField
              {...params}
              label={translate(TranslationEnum.update_configuration,"Select Domain Name")}
              error={error && !updateRequestBody.domainName}
              helperText={error && !updateRequestBody.domainName && "required*"}
            />
          )}
        />
        <Autocomplete
          sx={{ width: "100%" }}
          disableClearable
          value={updateRequestBody.domainType || ""}
          onChange={(event, value: string | null) => {
            if (value) {
              setError(false);
              const jsonData = allConfigurations?.find((eachConfig: any) => {
                return (
                  eachConfig.domainType === value &&
                  eachConfig.domainName === updateRequestBody.domainName
                );
              });
              const domainValuesString = JSON.stringify(
                jsonData.domainValues,
                null,
                2
              );
              setUpdateRequestBody({
                ...updateRequestBody,
                domainType: value,
                domainValues: domainValuesString,
              });
            }
          }}
          options={domainValueOptions ? domainValueOptions : []}
          renderInput={(params) => (
            <TextField
              {...params}
              label={translate(TranslationEnum.update_configuration,"Select Domain Type")}
              error={error && !updateRequestBody.domainType}
              helperText={error && !updateRequestBody.domainType && "required*"}
            />
          )}
        />
        <TextField
          id="outlined-multiline-flexible"
          label={translate(TranslationEnum.update_configuration,"DomainValues")}
          multiline
          rows={8}
          InputLabelProps={{
            shrink: updateRequestBody?.domainValues ? true : false,
          }}
          maxRows={8}
          value={updateRequestBody.domainValues}
          onChange={(e) => {
            setError(false);
            setUpdateRequestBody({
              ...updateRequestBody,
              domainValues: e.target.value,
            });
          }}
          error={error && !isJsonString(updateRequestBody.domainValues)}
          helperText={
            error &&
            !isJsonString(updateRequestBody.domainValues) &&
            "Please enter a valid json"
          }
        />
        <div className="update-configuration-buttons">
          <button
            className="update-configuration-update-button"
            onClick={() => {
              if (checkAllfieldsPresent()) {
                sendUpdateRequest("update")
              }
            }}
          >
             {translate(TranslationEnum.common_portal,"UPDATE")}
          </button>
          <button
            className="update-configuration-delete-button"
            onClick={() => {
              if (checkAllfieldsPresent()) {
                setConfirmationPopUp(true);
                updateConfirmationPopData(
                  "Are you sure you want to delete this Configuration",
                  () => {
                    sendUpdateRequest("delete");
                  }
                );
              }
            }}
          >
             {translate(TranslationEnum.common_portal,"DELETE")}
          </button>
        </div>
      </div>
    </>
  );
};

const NewConfiguration = ({
  openPopUp,
  isJsonString,
  getAllConfiguartionsData,
  editRequestBody: { newConfigRequestBody, setNewConfigRequestBody },
}: newConfigurationType) => {
  const [error, setError] = useState(false);
  const { translate } = usePortalTranslation();
  const checkAllFieldsPresent = () => {
    if (
      !isJsonString(newConfigRequestBody.domainValues) ||
      !newConfigRequestBody.domainType ||
      !newConfigRequestBody.domainName
    ) {
      setError(true);
      return false;
    }
    return true;
  };

  const createNewConfig = async () => {
    try {
      const api = "/metadata";
      const apiType = "POST";
      let parsedJsonValue = JSON.parse(newConfigRequestBody.domainValues);
    
      const isArray = Array.isArray(parsedJsonValue);
      if(!isArray){
        parsedJsonValue = [parsedJsonValue];
      }

      const requestBody = {
        lob: getLob(),
        domainName: newConfigRequestBody.domainName,
        domainType: newConfigRequestBody.domainType,
        domainValues: parsedJsonValue,
      };

      const requestArray: updateRequestBody[] = [];
      requestArray.push(requestBody);
      validateWithOtp((verifiedUser?: manageUpdateAccessObj,verifyResponse?: any) => {
        try{
          if (verifyResponse.status === 201 || verifyResponse.status === 200) {
            sendConfigAnalyticsEvent("config_created",requestBody,verifyResponse,"Kpi-dashboard-updateMetaConfig",verifiedUser)
            openPopUp("Your information has been updated!", "Success");
            getAllConfiguartionsData();
          } else {
            throw new Error();
          }
        }catch(error){
          openPopUp("Something went wrong!", "Error");
        }
      },undefined,"admin",api,requestArray,apiType);

    } catch (error) {
      openPopUp("Something went wrong!", "Error");
    }
  };

  return (
    <>
      <div className="update-configuration-label">
        <span>{translate(TranslationEnum.update_configuration,"New Configuration")}</span>
      </div>
      <div className="update-configuration-inputs">
        <TextField
          id="outlined-start-adornment"
          label={translate(TranslationEnum.update_configuration,"Domain Name")}
          onChange={(event) => {
            setError(false);
            setNewConfigRequestBody({
              ...newConfigRequestBody,
              domainName: event.target.value,
            });
          }}
          error={error && !newConfigRequestBody.domainName}
          helperText={error && !newConfigRequestBody.domainName && "required"}
        />
        <TextField
          id="outlined-start-adornment"
          label={translate(TranslationEnum.update_configuration,"Domain Type")}
          onChange={(event) => {
            setError(false);
            setNewConfigRequestBody({
              ...newConfigRequestBody,
              domainType: event.target.value,
            });
          }}
          error={error && !newConfigRequestBody.domainType}
          helperText={error && !newConfigRequestBody.domainType && "required"}
        />
        <TextField
          id="outlined-multiline-flexible"
          label="DomainValues"
          multiline
          rows={8}
          InputLabelProps={{
            shrink: newConfigRequestBody?.domainValues ? true : false,
          }}
          maxRows={8}
          onChange={(event) => {
            setError(false);
            setNewConfigRequestBody({
              ...newConfigRequestBody,
              domainValues: event.target.value,
            });
          }}
          error={error && !isJsonString(newConfigRequestBody.domainValues)}
          helperText={
            error &&
            !isJsonString(newConfigRequestBody.domainValues) &&
            "Please enter a valid json"
          }
        />
      </div>
      <div className="update-configuration-buttons">
        <button
          className="update-configuration-update-button"
          onClick={() => {
            if (checkAllFieldsPresent()) {
              createNewConfig();
            }
          }}
        >
          {translate(TranslationEnum.common_portal,"Send")}
        </button>
      </div>
    </>
  );
};

const UpdateMetaConfig = () => {
  const { translate } = usePortalTranslation();
  const [value, setValue] = React.useState("1");

  const [allConfigurations, setAllConfigurations] = useState<any | undefined>();

  const [loader, setLoader] = useState(true);

  useEffect(() => {
    getAllConfiguartionsData();
  }, []);

  const getAllConfiguartionsData = async () => {
    try {
      setLoader(true);
      const response = await getAllConfiguartions();
      if (response.status === 200) {
        const configurations = response?.data?.features;
        setAllConfigurations(configurations);
      }
      else{
        throw new Error();
      }
      setLoader(false);
    } catch (error) {
      setAllConfigurations([]);
      openPopUp("Something Went wrong", "Error");
    }
  };

  const isJsonString = (str: string) => {
    try {
      JSON.parse(str);
    } catch (e) {
      return false;
    }
    return true;
  };

  const [genericModalState, setGenericModalState] = useState(false);
  const [genericModalMessage, setGenericModalSMessage] = useState("");
  const [genericModaltype, setGenericModalType] = useState<popupType>("Error");

  const [confirmationPopUp, setConfirmationPopUp] = useState(false);
  const [popUpMessage, setPopUpMessage] = useState<string>("");
  const popUpSuccessMethod = useRef<any>();

  const updateConfirmationPopData = (
    message: string,
    methodHelper: () => void
  ) => {
    popUpSuccessMethod.current = methodHelper;
    setPopUpMessage(message);
  };

  const openPopUp = (message: string, modalType: popupType) => {
    setGenericModalSMessage(message);
    setGenericModalState(true);
    setGenericModalType(modalType);
  };

  const handleChange = (event: React.SyntheticEvent, newValue: string) => {
    setValue(newValue);
  };

  const [updateRequestBody, setUpdateRequestBody] = useState<updateRequestBody>(
    {
      domainName: "",
      domainType: "",
      domainValues: "",
    }
  );

  const [newConfigRequestBody, setNewConfigRequestBody] =
    useState<updateRequestBody>({
      domainName: "",
      domainType: "",
      domainValues: "",
    });

  const [domainValueOptions, setDomainValueOptions] = useState<[]>([]);

  return (
    <>
      <Box sx={{ width: "100%" }} className="update-meta-config-parent">
        <TabContext value={value}>
          <Box sx={{ borderColor: "divider" }}>
            <TabList
              onChange={handleChange}
              aria-label="lab API tabs example"
              variant="fullWidth"
            >
              <Tab label={translate(TranslationEnum.update_configuration,"Update Configuration")} value="1" />
              <Tab label={translate(TranslationEnum.update_configuration,"New Configuration")} value="2" />
            </TabList>
          </Box>
          <TabPanel value="1">
            {loader ? (
              <Loader />
            ) : (
              <UpdateConfiguration
                configurations={{ allConfigurations, setAllConfigurations }}
                openPopUp={openPopUp}
                isJsonString={isJsonString}
                setConfirmationPopUp={setConfirmationPopUp}
                updateConfirmationPopData={updateConfirmationPopData}
                editRequestBody={{ updateRequestBody, setUpdateRequestBody }}
                domainValuesDropDown={{
                  domainValueOptions,
                  setDomainValueOptions,
                }}
              />
            )}
          </TabPanel>
          <TabPanel value="2">
            {loader ? (
              <Loader />
            ) : (
              <NewConfiguration
                openPopUp={openPopUp}
                isJsonString={isJsonString}
                getAllConfiguartionsData={getAllConfiguartionsData}
                editRequestBody={{
                  newConfigRequestBody,
                  setNewConfigRequestBody,
                }}
              />
            )}
          </TabPanel>
        </TabContext>
      </Box>
      <GenericPopUp
        type={genericModaltype}
        message={genericModalMessage}
        openGenericModal={genericModalState}
        setOpenGenericModal={() => {
          setGenericModalState(false);
        }}
      />
      <ConfirmationPopUp
        openConfirmModal={confirmationPopUp}
        message={popUpMessage}
        setOpenConfirmModal={() => {
          setConfirmationPopUp(false);
        }}
        successMethod={popUpSuccessMethod.current}
      />
    </>
  );
};

export default UpdateMetaConfig;
